import React, { useEffect, useState } from "react";
import { maintenanceApi, ProcBatch } from "../../../api/maintenance";
import { STAGES, fmtDate } from "./procUtils";

interface Props {
  refreshKey: number;
}

const BAR_COLOR: Record<string, string> = {
  generated: "#1565c0",
  dispatched: "#e65100",
  received: "#6a3fb5",
  onboarded: "#2e7d32",
  retired: "#c62828",
};

/** Batches — where the PO / vendor / model live once, instead of on 100 device rows. */
const BatchesTab: React.FC<Props> = ({ refreshKey }) => {
  const [batches, setBatches] = useState<ProcBatch[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    maintenanceApi
      .procGetBatches()
      .then((r) => setBatches(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [refreshKey]);

  if (loading) return <p className="maint-loading">Loading…</p>;

  return (
    <div className="proc-mfr-grid">
      {batches.map((b) => {
        const counts: Record<string, number> = {
          generated: b.generated,
          dispatched: b.dispatched,
          received: b.received,
          onboarded: b.onboarded,
          retired: b.retired,
        };
        const tracked = Object.values(counts).reduce((a, c) => a + c, 0) || 1;
        return (
          <div className="proc-mfr-card" key={b.id}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
              <div>
                <div className="mn">{b.name}</div>
                <div className="proc-mini">
                  {b.manufacturer_name || "no manufacturer"}
                  {b.hardware_model ? ` · ${b.hardware_model}` : ""}
                </div>
              </div>
              <span className="proc-pill grey">{tracked} units</span>
            </div>

            <div className="proc-bar">
              {STAGES.map((s) => {
                const pct = (counts[s.key] / tracked) * 100;
                return pct > 0 ? (
                  <span key={s.key} style={{ width: `${pct}%`, background: BAR_COLOR[s.key] }} />
                ) : null;
              })}
            </div>

            <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 14px" }} className="proc-mini">
              {STAGES.map((s) => (
                <span key={s.key} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span className={`proc-dot ${s.dot}`} />
                  {s.label} <b style={{ color: "#333" }}>{counts[s.key]}</b>
                </span>
              ))}
            </div>

            <div
              style={{
                borderTop: "1px solid #f0f0f0",
                marginTop: 12,
                paddingTop: 10,
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "6px 14px",
                fontSize: 12.5,
              }}
            >
              <span style={{ color: "#999" }}>PO / order</span>
              <span style={{ fontWeight: 500 }}>{b.po_ref || "—"}</span>
              <span style={{ color: "#999" }}>Connector</span>
              <span style={{ fontWeight: 500 }}>{b.connector_std || "—"}</span>
              <span style={{ color: "#999" }}>Power</span>
              <span style={{ fontWeight: 500 }}>{b.power_kw ? `${b.power_kw} kW` : "—"}</span>
              <span style={{ color: "#999" }}>Minted</span>
              <span style={{ fontWeight: 500 }}>{fmtDate(b.minted_on)}</span>
            </div>

            {b.notes && <div className="proc-note">{b.notes}</div>}
          </div>
        );
      })}
      {batches.length === 0 && <p className="maint-empty">No batches yet.</p>}
    </div>
  );
};

export default BatchesTab;
